/**
 * Informe del recorrido — órdenes vinculadas (cross-link 3, 2026-09-22).
 * Funciones PURAS: el reparto de trabajos (atribución del back) y la primera
 * comunicación, para que la E2E los pruebe sin red.
 */

import { isWorkState } from '../../constants/workStates';

export type LinkedWorkCandidate = {
  id: string;
  type?: string | null;
  registeredByUserId?: string | null;
  registerInitDate?: string | Date | null;
  /** `partial` (borrador) y `annulled` no son trabajo: `isWorkState`. */
  state?: string | null;
};

/**
 * Spec 2026-09-23 § 7: el informe usa la MISMA atribución que la pill de la
 * card (`workOrderIds` del back: equipo + franja, desempate por tripulación).
 * Devuelve los trabajos de esta patrulla y cuántas actuaciones o accidentes
 * de la OT no lo son (otros equipos u otros turnos).
 */
export function splitLinkedWorks<T extends LinkedWorkCandidate>(
  children: T[],
  workOrderIds: Iterable<string>,
): { kept: T[]; otherCount: number } {
  const ids = new Set(workOrderIds);
  // Un borrador (asistente sin terminar) no es trabajo de nadie: ni de esta
  // patrulla ni «otro» (spec 2026-09-23, actuaciones parciales).
  // Una anulada tampoco (spec § 15, 2026-09-24): es lo que deja una
  // transferencia en la OT de origen, y el back ya no la atribuye.
  // La regla es la compartida de todos los informes (decisión D3).
  const works = children.filter(
    (c) =>
      (c.type === 'action' || c.type === 'accident') && isWorkState(c.state),
  );
  const kept = works.filter((w) => ids.has(w.id));
  return { kept, otherCount: works.length - kept.length };
}

/** La primera comunicación de una OT: la hija `communication` con menor fecha de registro (respaldo: creación). */
export function firstCommunicationOf<
  T extends {
    id: string;
    type?: string | null;
    registerInitDate?: string | null;
    creationDate?: string | null;
  },
>(children: T[]): T | null {
  const comms = children.filter((c) => c.type === 'communication');
  if (comms.length === 0) return null;
  const at = (c: T) =>
    new Date(c.registerInitDate ?? c.creationDate ?? 0).getTime() || 0;
  return comms.slice().sort((a, b) => at(a) - at(b))[0];
}

const EMPTY = '—';

/**
 * Datos de la OT vinculada en el informe: ROID, tipo, estado, descripción y
 * los tramos (avisos) de este recorrido ligados a ella. Sin «Vínculo:
 * automático/manual» (spec § 15, 2026-09-24): a quien lee el informe no le
 * dice nada — el dato sigue guardado para diagnosticar un vínculo raro.
 */
export function linkedOrderInfoRows(
  ot: {
    roid?: string | null;
    type?: string | null;
    state?: string | null;
    description?: string | null;
  },
  segments: { id?: string | null; indexType?: number | string | null }[],
  t: (key: string) => string,
  translateState: (state: string) => string,
  formatRoid: (roid: string | undefined) => string = (roid) => roid ?? EMPTY,
  /**
   * Nº de cada tramo en la tabla del informe (plan 2026-10-08 D5). El número
   * interno (`indexType`) no sigue el orden de la tabla tras registrar una
   * parada o transferir: la pieza y el transferido llevan el siguiente libre.
   */
  tableNumberById?: Map<string, number>,
): { label: string; value: string }[] {
  const numbers = segments
    .map(
      (sg) => (sg.id ? tableNumberById?.get(sg.id) : undefined) ?? sg.indexType,
    )
    .filter((n): n is number | string => n != null)
    .sort((a, b) => Number(a) - Number(b));
  return [
    { label: 'ROID', value: formatRoid(ot.roid ?? undefined) },
    { label: t('tables:headers.type'), value: t(`common:orderTypes.${ot.type}`) },
    { label: t('tables:headers.status'), value: translateState(ot.state ?? '') },
    {
      label: t('common:tables.headers.description'),
      value: ot.description ?? EMPTY,
    },
    {
      label: t('modals:surveillanceReport.segmentsTitle'),
      value: numbers.join(', ') || EMPTY,
    },
  ];
}
